import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  CasinoOutlined,
  CalculateOutlined,
  FavoriteBorderOutlined,
  HelpOutline,
  InsightsOutlined,
  SettingsOutlined,
  SportsSoccerOutlined,
  StorageOutlined,
  GroupsOutlined,
  GridOnOutlined,
  TimelineOutlined,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const SYSTEM_BET_CALCULATOR_URL =
  "https://paulsen.blog/apps/systemwettenrechner/index.html";
export const CONTROL_CENTER_NAV_HEIGHT = 56;

export function ControlCenterNavigation({
  activeArea,
  onReturnLegacy,
  onControlCenter,
  onGames,
  onTeams,
  onStatistics,
  onSimulation,
  onBetting,
  onDataMaintenance,
  onSettings,
  onHelp,
}) {
  const { t } = useTranslation();
  const items = [
    { key: "controlCenter", label: t("help.controlCenter.navigation.controlCenter"), onClick: onControlCenter },
    { key: "games", label: t("help.controlCenter.navigation.games"), onClick: onGames },
    { key: "teams", label: t("help.controlCenter.navigation.teams"), onClick: onTeams },
    { key: "statistics", label: t("help.controlCenter.navigation.statistics"), onClick: onStatistics },
    { key: "simulation", label: t("help.controlCenter.navigation.simulation"), onClick: onSimulation },
    { key: "betting", label: t("help.controlCenter.navigation.betting"), onClick: onBetting },
    {
      key: "systemBetCalculator",
      label: t("help.controlCenter.navigation.systemBetCalculator"),
      href: SYSTEM_BET_CALCULATOR_URL,
    },
    { key: "dataMaintenance", label: t("help.controlCenter.navigation.dataMaintenance"), onClick: onDataMaintenance },
    { key: "settings", label: t("help.controlCenter.navigation.settings"), onClick: onSettings },
    { key: "help", label: t("help.controlCenter.navigation.help"), onClick: onHelp },
  ];

  return (
    <Paper
      component="nav"
      square
      elevation={2}
      aria-label={t("help.controlCenter.navigation.label")}
      sx={{
        position: "fixed",
        inset: "0 0 auto 0",
        zIndex: (theme) => theme.zIndex.modal + 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.25}
        sx={{ minHeight: CONTROL_CENTER_NAV_HEIGHT, px: { xs: 1, md: 2 }, overflowX: "auto" }}
      >
        <Button
          size="small"
          color="primary"
          onClick={onReturnLegacy}
          sx={{ whiteSpace: "nowrap", mr: 0.75 }}
        >
          {t("help.controlCenter.returnLegacy")}
        </Button>
        {items.map((item) => (
          <Button
            key={item.key}
            size="small"
            variant={activeArea === item.key ? "contained" : "text"}
            onClick={item.onClick}
            href={item.href}
            target={item.href ? "_blank" : undefined}
            rel={item.href ? "noreferrer" : undefined}
            sx={{ whiteSpace: "nowrap", textTransform: "none" }}
          >
            {item.label}
          </Button>
        ))}
      </Stack>
    </Paper>
  );
}

function ControlCenterTile({ icon, title, description, onClick, href, status, actions = [] }) {
  const content = (
    <CardContent sx={{ p: { xs: 2.25, md: 2.75 }, height: "100%" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            width: 46,
            height: 46,
            borderRadius: 2.5,
            bgcolor: "rgba(123,60,89,0.1)",
            color: "primary.main",
          }}
        >
          {icon}
        </Box>
        {status}
      </Stack>
      <Typography variant="h6" sx={{ mt: 2, fontWeight: 800, letterSpacing: "0.025em" }}>
        {title}
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55 }}>
        {description}
      </Typography>
    </CardContent>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        minHeight: 220,
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        borderColor: "rgba(123,60,89,0.2)",
        bgcolor: "rgba(255,255,255,0.94)",
        boxShadow: "0 8px 24px rgba(74,35,54,0.07)",
        transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 16px 34px rgba(74,35,54,0.16)",
          borderColor: "primary.light",
        },
      }}
    >
      {onClick || href ? (
        <CardActionArea
          onClick={onClick}
          href={href}
          target={href ? "_blank" : undefined}
          rel={href ? "noreferrer" : undefined}
          sx={{ flex: 1, alignItems: "stretch" }}
        >
          {content}
        </CardActionArea>
      ) : content}
      {actions.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ px: 2.25, pb: 2 }}>
          {actions.map((action) => (
            <Button
              key={action.label}
              size="small"
              onClick={action.onClick}
              href={action.href}
              target={action.href ? "_blank" : undefined}
              rel={action.href ? "noreferrer" : undefined}
            >
              {action.label}
            </Button>
          ))}
        </Stack>
      )}
    </Card>
  );
}

export function ControlCenter({
  onReturnLegacy,
  onGames,
  onTeams,
  onStandings,
  onStatistics,
  onSimulation,
  onBetting,
  onSettings,
  onDataMaintenance,
  onWhatsNew,
  onChess,
  isAdmin,
  dataMaintenanceAvailable,
  hasNewWhatsNew,
  plansUrl,
  donateUrl,
}) {
  const { t } = useTranslation();
  const tiles = [
    { key: "games", icon: <SportsSoccerOutlined />, onClick: onGames },
    { key: "teams", icon: <GroupsOutlined />, onClick: onTeams },
    {
      key: "statistics",
      icon: <InsightsOutlined />,
      onClick: onStatistics,
      actions: [{ label: t("help.controlCenter.actions.standings"), onClick: onStandings }],
    },
    { key: "simulation", icon: <TimelineOutlined />, onClick: onSimulation },
    { key: "betting", icon: <CasinoOutlined />, onClick: onBetting },
    {
      key: "systemBetCalculator",
      icon: <CalculateOutlined />,
      href: SYSTEM_BET_CALCULATOR_URL,
    },
    {
      key: "dataMaintenance",
      icon: <StorageOutlined />,
      onClick: onDataMaintenance,
      status: (
        <Chip
          size="small"
          color={dataMaintenanceAvailable ? "warning" : "success"}
          label={t(dataMaintenanceAvailable
            ? "help.controlCenter.maintenanceAvailable"
            : "help.controlCenter.maintenanceClean")}
        />
      ),
    },
    { key: "settings", icon: <SettingsOutlined />, onClick: onSettings },
    {
      key: "helpNews",
      icon: <HelpOutline />,
      onClick: onWhatsNew,
      status: hasNewWhatsNew ? <Chip size="small" color="secondary" label="NEW" /> : null,
      actions: [{ label: t("help.controlCenter.actions.help"), href: "help" }],
    },
    {
      key: "planSupport",
      icon: <FavoriteBorderOutlined />,
      href: plansUrl,
      actions: [{ label: t("help.controlCenter.actions.support"), href: donateUrl }],
    },
  ];

  if (isAdmin) {
    tiles.push({
      key: "chess",
      icon: <GridOnOutlined />,
      onClick: onChess,
    });
  }

  return (
    <Box sx={{ maxWidth: 1440, mx: "auto", pb: 5 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3.5 }}
      >
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, color: "primary.main" }}>
            {t("help.controlCenter.title")}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 720 }}>
            {t("help.controlCenter.subtitle")}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={onReturnLegacy} sx={{ whiteSpace: "nowrap" }}>
          {t("help.controlCenter.returnLegacy")}
        </Button>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
          },
          gap: { xs: 2, md: 2.5 },
        }}
      >
        {tiles.map((tile) => (
          <ControlCenterTile
            key={tile.key}
            icon={tile.icon}
            title={t(`help.controlCenter.tiles.${tile.key}.title`)}
            description={t(`help.controlCenter.tiles.${tile.key}.description`)}
            onClick={tile.onClick}
            href={tile.href}
            status={tile.status}
            actions={tile.actions}
          />
        ))}
      </Box>
    </Box>
  );
}
